import type {
  AppointmentPlanningFlexMode,
  AppointmentPlanningKeptSettings,
  AppointmentPlanningSettingsSnapshot,
  AppointmentPlanningWorkOrderOverride,
} from "../../../_logic/appointments-contract"
import { formatDateOnly } from "../../../_logic/dates"
import { compactAppointmentPlanningOverrides } from "./rowOverrides"

export type AppointmentPlanningEmployeeOption = {
  label: string
  value: string | number
}

/** Offer employees eligible for at least one order; an unrestricted order allows all options. */
export const employeeOptionsForWorkOrders = <TOption extends AppointmentPlanningEmployeeOption>(
  employeeOptions: TOption[],
  workOrders: Pick<WorkOrder, "allowed_employee_ids">[],
): TOption[] => {
  if (!workOrders.length || workOrders.some((order) => !order.allowed_employee_ids?.length))
    return employeeOptions

  const allowed = new Set(
    workOrders.flatMap((order) => order.allowed_employee_ids ?? []).map(String),
  )

  return employeeOptions.filter((option) => allowed.has(String(option.value)))
}

export const employeeIdsFromOptions = (
  employeeOptions: AppointmentPlanningEmployeeOption[],
): string[] => employeeOptions.map((entry) => String(entry.value)).filter(Boolean)

export const normalizeEmployeeSelection = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map(String)
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []

export const restrictEmployeeSelectionToOptions = (
  value: unknown,
  employeeIds: string[],
): string[] => {
  const available = new Set(employeeIds.map(String).filter(Boolean))

  if (!available.size) return []

  return normalizeEmployeeSelection(value).filter((entry) => available.has(entry))
}

export const selectedEmployeeValuesFromEvent = (event?: Event): string[] | null => {
  const detail = (event as CustomEvent<{ values?: unknown[]; value?: unknown }> | undefined)?.detail

  if (!detail) return null

  if (Array.isArray(detail.values)) return normalizeEmployeeSelection(detail.values)

  return normalizeEmployeeSelection([detail.value])
}

export const resolveSelectedEmployees = ({
  value,
  employeeIds,
}: {
  value: unknown
  employeeIds: string[]
}): string[] => {
  const availableSet = new Set(employeeIds)
  const normalized = normalizeEmployeeSelection(value)
  const scoped = normalized.filter((entry) => availableSet.has(entry))

  return scoped.length ? scoped : employeeIds
}

export const buildInitialAppointmentPlanningSettings = ({
  initialSettings,
  employeeIds,
  defaultStartDate,
  defaultPlanFlexAppointments = false,
}: {
  initialSettings?: AppointmentPlanningSettingsSnapshot | null
  employeeIds: string[]
  defaultStartDate: string
  defaultPlanFlexAppointments?: boolean
}): AppointmentPlanningSettingsSnapshot =>
  initialSettings ?? {
    workOrderOverridesByRowId: {},
    startDate: defaultStartDate,
    selectedEmployees: employeeIds,
    planFlexAppointments: defaultPlanFlexAppointments,
    flexPlanningMode: defaultPlanFlexAppointments ? "plan" : "skip",
  }

export const normalizePendingSelectedEmployees = ({
  value,
  allEmployeeIds,
}: {
  value: string[]
  allEmployeeIds: string[]
}): string[] => {
  const nextSelected = normalizeEmployeeSelection(value)
  const available = new Set(allEmployeeIds)
  const scoped = nextSelected.filter((id) => available.has(id))

  return scoped.length ? scoped : Array.from(available)
}

export const buildAppointmentPlanningSettingsSnapshot = ({
  workOrderOverridesByRowId,
  startDate,
  selectedEmployees,
  flexPlanningMode,
}: {
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  startDate: string
  selectedEmployees: string[]
  flexPlanningMode: AppointmentPlanningFlexMode
}): AppointmentPlanningSettingsSnapshot => ({
  workOrderOverridesByRowId,
  startDate,
  selectedEmployees,
  planFlexAppointments: flexPlanningMode !== "skip",
  flexPlanningMode,
})

const normalizeFlexPlanningMode = (
  flexPlanningAvailable: boolean,
  value: AppointmentPlanningFlexMode,
): AppointmentPlanningFlexMode =>
  flexPlanningAvailable && (value === "plan" || value === "every_second") ? value : "skip"

export const flexAppointmentPlanningModeForPayload = (
  mode: AppointmentPlanningFlexMode,
): "DISABLED" | "ALL" | "EVERY_SECOND" =>
  mode === "plan" ? "ALL" : mode === "every_second" ? "EVERY_SECOND" : "DISABLED"

const flexPlanningModeFromPayload = (
  mode: AppointmentPlanningWorkOrderOverride["flex_appointment_mode"],
): AppointmentPlanningFlexMode | null =>
  mode === "ALL"
    ? "plan"
    : mode === "EVERY_SECOND"
      ? "every_second"
      : mode === "DISABLED"
        ? "skip"
        : null

export const appointmentPlanningFlexModeFromSettings = ({
  settings,
  flexPlanningAvailable,
}: {
  settings: AppointmentPlanningSettingsSnapshot
  flexPlanningAvailable: boolean
}): AppointmentPlanningFlexMode => {
  if (!flexPlanningAvailable) return "skip"

  const mode = settings.flexPlanningMode

  if (mode === "plan" || mode === "every_second" || mode === "skip") return mode

  return settings.planFlexAppointments ? "plan" : "skip"
}

export const resolveFlexAppointmentMode = ({
  override,
  flexPlanningAvailable,
  defaultFlexPlanningMode,
}: {
  override?: AppointmentPlanningWorkOrderOverride | null
  flexPlanningAvailable: boolean
  defaultFlexPlanningMode: AppointmentPlanningFlexMode
}): AppointmentPlanningFlexMode => {
  if (!flexPlanningAvailable) return "skip"

  const explicitMode = flexPlanningModeFromPayload(override?.flex_appointment_mode)

  if (explicitMode) return explicitMode

  if (typeof override?.flex_appointment_allowed === "boolean") {
    return override.flex_appointment_allowed ? "plan" : "skip"
  }

  return normalizeFlexPlanningMode(true, defaultFlexPlanningMode)
}

const setFlexPlanningOverridesForApply = ({
  rowIds,
  targetIds,
  currentOverrides,
  currentFlexPlanningMode,
  flexPlanningAvailable,
}: {
  rowIds: string[]
  targetIds: string[]
  currentOverrides: Record<string, AppointmentPlanningWorkOrderOverride>
  currentFlexPlanningMode: AppointmentPlanningFlexMode
  flexPlanningAvailable: boolean
}): Record<string, AppointmentPlanningWorkOrderOverride> => {
  if (!flexPlanningAvailable) {
    return Object.fromEntries(
      Object.entries(currentOverrides).map(([rowId, override]) => {
        const next = { ...override }
        delete next.flex_appointment_allowed
        delete next.flex_appointment_mode

        return [rowId, next]
      }),
    )
  }

  const targetIdSet = new Set(targetIds)
  const appliesToEveryRow = rowIds.length > 0 && rowIds.every((rowId) => targetIdSet.has(rowId))

  if (appliesToEveryRow) {
    return Object.fromEntries(
      Object.entries(currentOverrides).map(([rowId, override]) => {
        const next = { ...override }
        delete next.flex_appointment_allowed
        delete next.flex_appointment_mode

        return [rowId, next]
      }),
    )
  }

  const nextOverrides = { ...currentOverrides }
  for (const rowId of rowIds) {
    const currentOverride = nextOverrides[rowId] ?? {}
    const nextOverride = { ...currentOverride }

    if (targetIdSet.has(rowId)) {
      delete nextOverride.flex_appointment_allowed
      delete nextOverride.flex_appointment_mode
    } else {
      const effectiveMode = resolveFlexAppointmentMode({
        override: currentOverride,
        flexPlanningAvailable,
        defaultFlexPlanningMode: currentFlexPlanningMode,
      })
      nextOverride.flex_appointment_mode = flexAppointmentPlanningModeForPayload(effectiveMode)
      delete nextOverride.flex_appointment_allowed
    }

    nextOverrides[rowId] = nextOverride
  }

  return nextOverrides
}

export const noKeptAppointmentPlanningSettings = (): AppointmentPlanningKeptSettings => ({
  startDate: false,
  selectedEmployees: false,
  flexPlanningMode: false,
})

/** Applies the preconfiguration; settings on "Unverändert lassen" keep their current value. */
export const applyAppointmentPlanningSettings = ({
  rowIds,
  targetIds,
  applySelectedIds,
  keptSettings,
  currentStartDate,
  pendingStartDate,
  currentSelectedEmployees,
  pendingSelectedEmployees,
  currentFlexPlanningMode,
  pendingFlexPlanningMode,
  flexPlanningAvailable,
  workOrderOverridesByRowId,
  allEmployeeIds,
}: {
  rowIds: string[]
  targetIds: string[]
  applySelectedIds: string[]
  keptSettings: AppointmentPlanningKeptSettings
  currentStartDate: string
  pendingStartDate: string
  currentSelectedEmployees: string[]
  pendingSelectedEmployees: string[]
  currentFlexPlanningMode: AppointmentPlanningFlexMode
  pendingFlexPlanningMode: AppointmentPlanningFlexMode
  flexPlanningAvailable: boolean
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  allEmployeeIds: string[]
}) => {
  const targetIdSet = new Set(targetIds)
  const nextOverrides = keptSettings.flexPlanningMode
    ? { ...workOrderOverridesByRowId }
    : setFlexPlanningOverridesForApply({
        rowIds,
        targetIds,
        currentOverrides: workOrderOverridesByRowId,
        currentFlexPlanningMode,
        flexPlanningAvailable,
      })

  if (
    !keptSettings.selectedEmployees &&
    applySelectedIds.length &&
    pendingSelectedEmployees.length === 1
  ) {
    const employeeId = pendingSelectedEmployees[0]
    for (const rowId of targetIdSet) {
      nextOverrides[rowId] = {
        ...(nextOverrides[rowId] ?? {}),
        appointment_employee_id: employeeId,
      }
    }
  }

  return {
    workOrderOverridesByRowId: compactAppointmentPlanningOverrides(nextOverrides),
    startDate: formatDateOnly(keptSettings.startDate ? currentStartDate : pendingStartDate),
    selectedEmployees: normalizePendingSelectedEmployees({
      value: keptSettings.selectedEmployees ? currentSelectedEmployees : pendingSelectedEmployees,
      allEmployeeIds,
    }),
    flexPlanningMode: normalizeFlexPlanningMode(
      flexPlanningAvailable,
      keptSettings.flexPlanningMode ? currentFlexPlanningMode : pendingFlexPlanningMode,
    ),
  }
}
